import {
  CustomerUpdate,
  CustomerUpdateStatus,
  CustomerUpdateStatusLabels,
  IconNames,
} from "@/types/v2";
import { firestoreToDateTime, cn } from "@/lib/utils";
import { addMinutes, format } from "date-fns";
import { useState } from "react";
import { Badge } from "@/components/Base/Badge";
import { Icon } from "@/components/Common/Icon";
import { Button } from "@/components/Base/Button";
import {
  CustomerUpdateForm,
  CustomerUpdateFormValues,
} from "./CustomerUpdateForm";
import {
  CustomerUpdateUsageLocation,
  useCustomerUpdatesAnalytics,
} from "@/hooks/useCustomerUpdatesAnalytics";
import { useStore } from "@/stores/useStore";
import { useCustomerUpdate } from "@/hooks/useCustomerUpdate";
import { useServiceAdvisors } from "@/hooks/shop/useServiceAdvisors";
import { statusClasses } from "@/lib/customerUpdates";
import { useCustomerUpdates } from "@/hooks/useCustomerUpdates";
import { getCardSection, getCardSectionIcon } from "@/lib/cards";

interface CustomerUpdateListItemProps {
  update: CustomerUpdate;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

export const CustomerUpdateListItem = ({
  update,
  isExpanded,
  onToggleExpand,
}: CustomerUpdateListItemProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get store data needed for the form
  const shop = useStore((state) => state.shop);
  const serviceAdvisors = useServiceAdvisors();
  const { customerUpdatesCompletedOnTimeMin } = useCustomerUpdates();

  // Use customer update hook for form submission logic
  const {
    card,
    handleSubmit: handleCustomerUpdateSubmit,
    handleSnoozeUpdate,
    snoozeTimeMin,
    customerUpdateSubmitting,
  } = useCustomerUpdate(update.id);

  // Analytics hook
  const { logCustomerUpdateCompleted } = useCustomerUpdatesAnalytics();

  // Form submission handler
  const handleSubmit = async (values: CustomerUpdateFormValues) => {
    setIsSubmitting(true);
    try {
      logCustomerUpdateCompleted(
        CustomerUpdateUsageLocation.PANEL,
        values.userId !== update.userId,
        values.timeUntil
      );
      await handleCustomerUpdateSubmit(values);
      // Collapse the form after successful submission
      onToggleExpand();
    } catch (error) {
      console.error("Failed to submit customer update:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Snooze handler
  const handleSnooze = async () => {
    setIsSubmitting(true);
    try {
      await handleSnoozeUpdate();
      // Collapse the form after successful snooze
      onToggleExpand();
    } catch (error) {
      console.error("Failed to snooze customer update:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusIcon = (status: CustomerUpdateStatus) => {
    switch (status) {
      case CustomerUpdateStatus.PENDING:
        return IconNames.clockWarn;
      case CustomerUpdateStatus.SNOOZED:
        return IconNames.clockFade;
      case CustomerUpdateStatus.SCHEDULED:
        return IconNames.clockUp;
      case CustomerUpdateStatus.CREATED:
        return IconNames.clockAdd;
      case CustomerUpdateStatus.COMPLETED:
        return IconNames.circleCheck;
      case CustomerUpdateStatus.SKIPPED:
        return IconNames.clear;
      case CustomerUpdateStatus.CANCELLED:
        return IconNames.circleCheck;
      default:
        return IconNames.bell;
    }
  };

  const getDueDateTime = () => {
    if (update.scheduledUpdateTime) {
      return firestoreToDateTime(update.scheduledUpdateTime);
    }
    if (update.desiredUpdateTime) {
      return firestoreToDateTime(update.desiredUpdateTime);
    }
    return null;
  };

  const dueDateTime = getDueDateTime();
  const isOverdue =
    dueDateTime &&
    addMinutes(dueDateTime, customerUpdatesCompletedOnTimeMin) < new Date() &&
    update.status === CustomerUpdateStatus.PENDING;

  return (
    <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
      {/* Collapsed View */}
      <div
        className={cn(
          "cursor-pointer p-2 transition-colors hover:bg-muted/50",
          isExpanded && "border-b"
        )}
        onClick={onToggleExpand}
      >
        <div className="flex flex-col items-start justify-start space-y-1">
          <div className="w-full">
            {/* Header Row */}
            <div className="flex w-full flex-grow items-center gap-2">
              {isOverdue ? (
                <Badge
                  variant="destructive"
                  className="flex items-center gap-1"
                >
                  <Icon name={IconNames.warning} size={12} />
                  Overdue
                </Badge>
              ) : (
                <Badge
                  className={cn(
                    "flex items-center gap-1",
                    statusClasses[update.status]
                  )}
                >
                  <Icon name={getStatusIcon(update.status)} size={12} />
                  {CustomerUpdateStatusLabels[update.status]}
                </Badge>
              )}
              {/* Due Date/Time */}
              <div className="flex-grow"></div>
              {dueDateTime && (
                <div className="flex items-center gap-2">
                  <Icon
                    name={IconNames.clock}
                    size={14}
                    className={cn(
                      "text-muted-foreground",
                      isOverdue && "text-destructive"
                    )}
                  />
                  <span
                    className={cn(
                      "text-sm",
                      isOverdue
                        ? "font-medium text-destructive"
                        : "text-muted-foreground"
                    )}
                  >
                    Due: {format(dueDateTime, "Pp")}
                  </span>
                </div>
              )}
              {/* Expand/Collapse Button */}
              <Button variant="ghost" size="smIcon" className="ml-2">
                <Icon
                  name={
                    isExpanded ? IconNames.chevronUp : IconNames.chevronDown
                  }
                  size={16}
                />
              </Button>
            </div>
          </div>

          {/* Customer and Vehicle Info */}
          {isExpanded ? null : (
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Icon
                  name={getCardSectionIcon(
                    shop?.cardConfig ?? {
                      cardContents: { primaryField: "Customer Name" },
                    },
                    "primaryField"
                  )}
                  size={14}
                  className="text-muted-foreground"
                />
                {card && shop ? (
                  <>
                    <span className="font-medium">
                      {getCardSection(card, shop, "primaryField")}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      • {getCardSection(card, shop, "tertiaryField")}
                    </span>
                  </>
                ) : (
                  <span>No Card Info</span>
                )}
              </div>
              {update.vehicle && (
                <div className="flex items-center gap-2">
                  <Icon
                    name={getCardSectionIcon(
                      shop?.cardConfig ?? {
                        cardContents: { secondaryField: "Customer Name" },
                      },
                      "secondaryField"
                    )}
                    size={14}
                    className="text-muted-foreground"
                  />
                  <span className="text-sm text-muted-foreground">
                    {card && shop ? (
                      getCardSection(card, shop, "secondaryField")
                    ) : (
                      <span>No Card Info</span>
                    )}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Expanded View */}
      {isExpanded && (
        <div className="p-4">
          {card && shop && serviceAdvisors ? (
            <CustomerUpdateForm
              initialValues={update}
              onSubmit={handleSubmit}
              onSnooze={handleSnooze}
              cardData={card}
              shop={shop}
              serviceAdvisors={serviceAdvisors}
              submitting={isSubmitting || customerUpdateSubmitting}
              snoozeTime={snoozeTimeMin}
              location={CustomerUpdateUsageLocation.PANEL}
            />
          ) : (
            <div className="text-sm text-muted-foreground">
              Loading customer update form...
            </div>
          )}
        </div>
      )}
    </div>
  );
};
